class KlipCouponPrice {
  constructor(section) {
    this.section = section;
    this.discount = null;
    this.renderedState = null;
    this.updateQueued = false;
    this.moneyFormat = section.getAttribute('data-money-format') || '{{amount}}';
    this.percentagePattern = /(\d+(?:[.,]\d+)?)\s*%/;
    this.fixedAmountPattern = /(?:€|EUR|CHF|£|\$)\s*(\d+(?:[.\s]\d{3})*(?:[.,]\d{1,2})?)|(\d+(?:[.\s]\d{3})*(?:[.,]\d{1,2})?)\s*(?:€|EUR|CHF|£|\$)/i;
    this.couponSelector = '.elsklip-coupon-box, .elsklip-coupon__container';

    this.onSectionChange = this.onSectionChange.bind(this);
    this.onSectionMutation = this.onSectionMutation.bind(this);

    this.section.addEventListener('change', this.onSectionChange);
    this.observer = new MutationObserver(this.onSectionMutation);
    this.observer.observe(this.section, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.queueUpdate();
  }

  onSectionChange() {
    this.queueUpdate();
  }

  onSectionMutation() {
    this.queueUpdate();
  }

  queueUpdate() {
    if (this.updateQueued) return;
    this.updateQueued = true;

    requestAnimationFrame(() => {
      this.updateQueued = false;
      this.update();
    });
  }

  update() {
    const couponBoxes = this.section.querySelectorAll(this.couponSelector);

    if (couponBoxes.length === 0) {
      this.discount = null;
      this.restorePrice();
      return;
    }

    couponBoxes.forEach((couponBox) => {
      const foundDiscount = this.readDiscount(couponBox);
      if (foundDiscount !== null) {
        this.discount = foundDiscount;
      }
    });

    const couponIsChecked = Array.from(couponBoxes).some((couponBox) => {
      return couponBox.querySelector('input[type="checkbox"]')?.checked;
    });

    if (!couponIsChecked || this.discount === null) {
      this.restorePrice();
      return;
    }

    const priceElement = this.section.querySelector('[id^="price-"] .price[data-price-cents]');
    if (!priceElement) {
      this.restorePrice();
      return;
    }

    const activePrice = parseInt(priceElement.getAttribute('data-price-cents'), 10);
    if (!Number.isFinite(activePrice)) {
      this.restorePrice();
      return;
    }

    const compareAtPrice = parseInt(priceElement.getAttribute('data-compare-at-cents'), 10);
    const crossedOutPrice = Number.isFinite(compareAtPrice) && compareAtPrice > activePrice
      ? compareAtPrice
      : activePrice;
    const discountedPrice = this.calculateDiscountedPrice(activePrice);
    this.renderCouponPrice(priceElement, crossedOutPrice, discountedPrice, this.discountKey());
  }

  readDiscount(couponBox) {
    const percentage = this.readPercentage(couponBox);
    if (percentage !== null && percentage > 0) {
      return {
        type: 'percentage',
        percentage: percentage,
      };
    }

    const amountCents = this.readFixedAmountCents(couponBox);
    if (amountCents !== null && amountCents > 0) {
      return {
        type: 'fixed',
        amountCents: amountCents,
      };
    }

    return null;
  }

  readPercentage(couponBox) {
    const match = couponBox.textContent.match(this.percentagePattern);
    if (!match) return null;

    const percentage = parseFloat(match[1].replace(',', '.'));
    if (!Number.isFinite(percentage)) return null;

    return percentage;
  }

  readFixedAmountCents(couponBox) {
    const match = couponBox.textContent.match(this.fixedAmountPattern);
    if (!match) return null;

    const rawAmount = match[1] || match[2];
    return this.amountToCents(rawAmount);
  }

  amountToCents(rawAmount) {
    let amount = rawAmount.replace(/\s/g, '');
    const hasComma = amount.indexOf(',') !== -1;
    const hasDot = amount.indexOf('.') !== -1;

    if (hasComma && hasDot) {
      if (amount.lastIndexOf(',') > amount.lastIndexOf('.')) {
        amount = amount.replace(/\./g, '').replace(',', '.');
      } else {
        amount = amount.replace(/,/g, '');
      }
    } else if (hasComma) {
      const commaParts = amount.split(',');
      const decimals = commaParts[commaParts.length - 1];
      if (commaParts.length > 1 && decimals.length === 3) {
        amount = amount.replace(/,/g, '');
      } else {
        amount = amount.replace(',', '.');
      }
    } else if (hasDot) {
      const dotParts = amount.split('.');
      const decimals = dotParts[dotParts.length - 1];
      if (dotParts.length > 1 && decimals.length === 3) {
        amount = amount.replace(/\./g, '');
      }
    }

    const value = parseFloat(amount);
    if (!Number.isFinite(value)) return null;

    return Math.round(value * 100);
  }

  calculateDiscountedPrice(originalPrice) {
    if (this.discount.type === 'fixed') {
      const discountedPrice = originalPrice - this.discount.amountCents;
      if (discountedPrice < 0) return 0;
      return discountedPrice;
    }

    const percentage = this.discount.percentage;
    const discountAmount = Math.floor(originalPrice * percentage / 100);
    const discountedPrice = originalPrice - discountAmount;
    if (discountedPrice < 0) return 0;
    return discountedPrice;
  }

  discountKey() {
    if (!this.discount) return '';
    if (this.discount.type === 'fixed') {
      return 'fixed:' + this.discount.amountCents;
    }
    return 'percentage:' + this.discount.percentage;
  }

  renderCouponPrice(priceElement, originalPrice, discountedPrice, percentage) {
    if (this.isSameRenderedState(priceElement, originalPrice, discountedPrice, percentage)) {
      return;
    }

    const existingPrice = priceElement.querySelector('.klip-coupon-price');
    if (existingPrice) {
      existingPrice.remove();
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'klip-coupon-price';

    const original = document.createElement('s');
    original.className = 'price-item klip-coupon-price__original';
    original.textContent = this.formatMoney(originalPrice);

    const discounted = document.createElement('span');
    discounted.className = 'price-item klip-coupon-price__discounted';
    discounted.textContent = this.formatMoney(discountedPrice);

    wrapper.appendChild(original);
    wrapper.appendChild(discounted);
    priceElement.appendChild(wrapper);
    priceElement.classList.add('price--klip-coupon');
    this.updateSaleBadge(priceElement, originalPrice, discountedPrice);

    this.renderedState = {
      priceElement: priceElement,
      originalPrice: originalPrice,
      discountedPrice: discountedPrice,
      percentage: percentage,
    };
  }

  isSameRenderedState(priceElement, originalPrice, discountedPrice, percentage) {
    if (!this.renderedState) return false;
    if (this.renderedState.priceElement !== priceElement) return false;
    if (this.renderedState.originalPrice !== originalPrice) return false;
    if (this.renderedState.discountedPrice !== discountedPrice) return false;
    if (this.renderedState.percentage !== percentage) return false;
    if (!priceElement.classList.contains('price--klip-coupon')) return false;
    if (!priceElement.querySelector('.klip-coupon-price')) return false;

    return true;
  }

  restorePrice() {
    const priceElements = this.section.querySelectorAll('[id^="price-"] .price[data-price-cents]');

    priceElements.forEach((priceElement) => {
      priceElement.classList.remove('price--klip-coupon');
      const customPrice = priceElement.querySelector('.klip-coupon-price');
      if (customPrice) {
        customPrice.remove();
      }
      this.restoreSaleBadge(priceElement);
    });

    this.renderedState = null;
  }

  updateSaleBadge(priceElement, originalPrice, discountedPrice) {
    const badge = priceElement.querySelector('.price__badge-sale');
    if (!badge) return;

    if (!badge.dataset.klipOriginalText) {
      badge.dataset.klipOriginalText = badge.textContent;
    }

    const compareAtCents = parseInt(priceElement.getAttribute('data-compare-at-cents'), 10);
    const basePrice = Number.isFinite(compareAtCents) && compareAtCents > discountedPrice
      ? compareAtCents
      : originalPrice;
    if (!basePrice || basePrice <= discountedPrice) return;

    const originalText = badge.dataset.klipOriginalText.trim();
    if (originalText.indexOf('Spare ') === 0) {
      badge.textContent = 'Spare ' + this.formatMoney(basePrice - discountedPrice);
      return;
    }

    const percent = Math.round(((basePrice - discountedPrice) * 100) / basePrice);
    badge.textContent = percent + '% Rabatt';
  }

  restoreSaleBadge(priceElement) {
    const badge = priceElement.querySelector('.price__badge-sale');
    if (!badge || !badge.dataset.klipOriginalText) return;

    badge.textContent = badge.dataset.klipOriginalText;
    delete badge.dataset.klipOriginalText;
  }

  formatMoney(cents) {
    const format = this.moneyFormat;
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholderMatch) return String(cents);

    const placeholder = placeholderMatch[1];
    let formattedAmount = this.formatAmount(cents, 2, ',', '.');

    if (placeholder === 'amount') {
      formattedAmount = this.formatAmount(cents, 2, ',', '.');
    } else if (placeholder === 'amount_no_decimals') {
      formattedAmount = this.formatAmount(cents, 0, ',', '.');
    } else if (placeholder === 'amount_with_comma_separator') {
      formattedAmount = this.formatAmount(cents, 2, '.', ',');
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      formattedAmount = this.formatAmount(cents, 0, '.', ',');
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      formattedAmount = this.formatAmount(cents, 2, "'", '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formattedAmount);
  }

  formatAmount(cents, precision, thousandsSeparator, decimalSeparator) {
    const isNegative = cents < 0;
    const absoluteCents = Math.abs(cents);
    const fixedAmount = (absoluteCents / 100).toFixed(precision);
    const parts = fixedAmount.split('.');
    const wholeAmount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    let formattedAmount = wholeAmount;
    if (precision > 0 && parts[1]) {
      formattedAmount = wholeAmount + decimalSeparator + parts[1];
    }

    if (isNegative) {
      formattedAmount = '-' + formattedAmount;
    }

    return formattedAmount;
  }
}

function initKlipCouponPrice() {
  const sections = document.querySelectorAll('[data-klip-coupon-price]');

  sections.forEach((section) => {
    if (section.klipCouponPrice) return;
    section.klipCouponPrice = new KlipCouponPrice(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKlipCouponPrice);
} else {
  initKlipCouponPrice();
}
